import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChatPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadData = async () => {
    try {
      const [userRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/chat/history`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setMessages(historyRes.data);
    } catch (error) {
      toast.error('Failed to load chat history');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat`,
        { message: userMessage },
        { withCredentials: true }
      );

      setMessages([...messages, {
        message_id: `temp_${Date.now()}`,
        message: userMessage,
        response: response.data.response,
        created_at: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="glass-nav border-b border-stone-200/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate('/patient-dashboard')}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-semibold text-text-primary">Health Assistant</h1>
                <p className="text-xs text-text-secondary font-body">AI-powered medical information</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto" data-testid="messages-container">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-accent" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-text-primary mb-2">Welcome to Your Health Assistant</h2>
              <p className="text-text-secondary font-body max-w-md mx-auto">
                Ask me questions about general health, wellness, medications, or symptoms. I'm here to provide information and guidance.
              </p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                <button
                  onClick={() => setInputMessage('What are the symptoms of the flu?')}
                  className="text-left p-4 bg-white border border-stone-100 rounded-lg hover:shadow-soft transition-shadow"
                >
                  <p className="font-body font-medium text-text-primary">What are the symptoms of the flu?</p>
                </button>
                <button
                  onClick={() => setInputMessage('How can I improve my sleep quality?')}
                  className="text-left p-4 bg-white border border-stone-100 rounded-lg hover:shadow-soft transition-shadow"
                >
                  <p className="font-body font-medium text-text-primary">How can I improve my sleep quality?</p>
                </button>
                <button
                  onClick={() => setInputMessage('What should I know about blood pressure?')}
                  className="text-left p-4 bg-white border border-stone-100 rounded-lg hover:shadow-soft transition-shadow"
                >
                  <p className="font-body font-medium text-text-primary">What should I know about blood pressure?</p>
                </button>
                <button
                  onClick={() => setInputMessage('Tips for staying healthy in winter?')}
                  className="text-left p-4 bg-white border border-stone-100 rounded-lg hover:shadow-soft transition-shadow"
                >
                  <p className="font-body font-medium text-text-primary">Tips for staying healthy in winter?</p>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((msg) => (
                <div key={msg.message_id} className="space-y-4">
                  {/* User Message */}
                  <div className="flex justify-end">
                    <div className="max-w-3xl bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-5 py-3" data-testid={`user-message-${msg.message_id}`}>
                      <p className="font-body">{msg.message}</p>
                    </div>
                  </div>
                  {/* AI Response */}
                  <div className="flex justify-start">
                    <div className="max-w-3xl bg-white border border-stone-100 rounded-2xl rounded-tl-sm px-5 py-4 shadow-sm" data-testid={`ai-response-${msg.message_id}`}>
                      <p className="font-body text-text-primary whitespace-pre-wrap leading-relaxed">{msg.response}</p>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="max-w-3xl bg-white border border-stone-100 rounded-2xl rounded-tl-sm px-5 py-4 shadow-sm">
                    <div className="flex gap-2">
                      <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input Form */}
      <div className="border-t border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <input
              data-testid="message-input"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask a health question..."
              className="flex-1 bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-full h-12 px-6 transition-all"
              disabled={loading}
            />
            <button
              data-testid="send-btn"
              type="submit"
              disabled={loading || !inputMessage.trim()}
              className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
          <p className="text-xs text-text-muted font-body mt-2 text-center">
            This AI assistant provides general information only. Always consult your doctor for medical advice.
          </p>
        </div>
      </div>
    </div>
  );
}